import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ApiError, api, loadCurrentUser, setCsrfToken } from "./api";
import { completeWorksSsoCallback } from "./sso";
import { AppShell, type Route } from "./components/shell";
import { LoginScreen } from "./features/auth";
import { BomsPage } from "./features/boms";
import { CalculationsPage } from "./features/calculations";
import { CatalogsPage } from "./features/catalogs";
import { DashboardPage } from "./features/dashboard";
import { AdminPage, HistoryPage } from "./features/history-admin";
import { InventoryPage } from "./features/inventory";
import type { CurrentUser } from "./types";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) =>
        !(error instanceof ApiError && error.status < 500) && count < 1,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});
const allowedRoutes = new Set<Route>([
  "overview",
  "boms",
  "calculations",
  "inventory",
  "catalogs",
  "history",
  "admin",
]);

function routeFromHash(): Route {
  const route = location.hash.slice(1) as Route;
  return allowedRoutes.has(route) ? route : "overview";
}

function App() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState<Route>(routeFromHash());
  useEffect(() => {
    void completeWorksSsoCallback()
      .then((ssoUser) => ssoUser ?? loadCurrentUser())
      .then(setUser)
      .catch(() => setCsrfToken(""))
      .finally(() => setLoading(false));
    const updateRoute = () => setRoute(routeFromHash());
    addEventListener("hashchange", updateRoute);
    return () => removeEventListener("hashchange", updateRoute);
  }, []);
  function navigate(next: Route) {
    location.hash = next;
    setRoute(next);
  }
  async function logout() {
    try {
      await api("/auth/logout", { method: "POST" });
    } finally {
      queryClient.clear();
      setCsrfToken("");
      setUser(null);
    }
  }
  if (loading)
    return (
      <div className="app-loading">
        <img src="/assets/vinh-hoan-logo.png?v=20260811" alt="Logo doanh nghiệp" />
        <span>Đang mở BOM System…</span>
      </div>
    );
  if (!user) return <LoginScreen onAuthenticated={setUser} />;
  const page =
    route === "overview" ? (
      <DashboardPage onNavigate={navigate} />
    ) : route === "boms" ? (
      <BomsPage roles={user.roles} />
    ) : route === "calculations" ? (
      <CalculationsPage />
    ) : route === "inventory" ? (
      <InventoryPage roles={user.roles} />
    ) : route === "catalogs" ? (
      <CatalogsPage />
    ) : route === "history" ? (
      <HistoryPage />
    ) : (
      <AdminPage />
    );
  return (
    <AppShell route={route} onNavigate={navigate} user={user} onLogout={logout}>
      {page}
    </AppShell>
  );
}

createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);
